import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const SEVERITY_COLORS = {
  CRITICAL: { rgb: [220, 53, 69], light: '#dc3545', label: 'Critical' },
  MAJOR: { rgb: [253, 126, 20], light: '#fd7e14', label: 'Major' },
  MINOR: { rgb: [255, 193, 7], light: '#ffc107', label: 'Minor' },
};

const STATUS_COLORS = {
  OPEN: { light: '#e7d4f5', dark: '#5a189a' },
  IN_PROGRESS: { light: '#e3f2fd', dark: '#1565c0' },
  RESOLVED: { light: '#e8f5e9', dark: '#2e7d32' },
  CLOSED: { light: '#f3e5f5', dark: '#6a1b9a' },
};

/**
 * Get severity color RGB array for jsPDF
 */
const getSeverityRGB = (severity) => {
  return SEVERITY_COLORS[severity]?.rgb || [0, 0, 0];
};

/**
 * Get severity hex color
 */
const getSeverityColor = (severity) => {
  return SEVERITY_COLORS[severity]?.light || '#000000';
};

/**
 * Get severity label
 */
const getSeverityLabel = (severity) => {
  return SEVERITY_COLORS[severity]?.label || severity;
};

/**
 * Generate PDF report with findings
 * @param {array} findings - Array of finding objects
 * @param {object} project - Project information { id, name }
 * @param {string} auditor - Auditor name
 * @returns {jsPDF} - PDF document
 */
export const generatePDF = (findings = [], project = {}, auditor = 'Auditor') => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 15;

  // Header
  doc.setFont('Arial', 'bold');
  doc.setFontSize(18);
  doc.text('Audit Findings Report', 15, yPosition);

  doc.setFont('Arial', 'normal');
  doc.setFontSize(10);
  yPosition += 10;
  doc.text(`Project: ${project.name || 'N/A'}`, 15, yPosition);
  yPosition += 5;
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 15, yPosition);
  yPosition += 5;
  doc.text(`Auditor: ${auditor}`, 15, yPosition);
  yPosition += 8;

  // Summary Statistics
  doc.setFont('Arial', 'bold');
  doc.setFontSize(12);
  doc.text('Summary Statistics', 15, yPosition);

  const stats = calculateStatistics(findings);
  yPosition += 8;
  doc.setFont('Arial', 'normal');
  doc.setFontSize(9);

  const statLabels = [
    `Total Findings: ${stats.total}`,
    `Critical: ${stats.critical}`,
    `Major: ${stats.major}`,
    `Minor: ${stats.minor}`,
    `Open: ${stats.open}`,
    `In Progress: ${stats.inProgress}`,
    `Resolved: ${stats.resolved}`,
    `Closed: ${stats.closed}`,
  ];

  const statsPerRow = 2;
  let statX = 15;
  for (let i = 0; i < statLabels.length; i++) {
    doc.text(statLabels[i], statX, yPosition);
    statX += pageWidth / 2 - 3;
    if ((i + 1) % statsPerRow === 0) {
      yPosition += 5;
      statX = 15;
    }
  }

  yPosition += 10;

  // Findings Table
  const findingsTableData = findings.map((finding) => [
    finding.standard || 'N/A',
    truncateText(finding.requirement, 30),
    getSeverityLabel(finding.severity),
    finding.status || 'N/A',
    finding.createdDate ? new Date(finding.createdDate).toLocaleDateString() : 'N/A',
  ]);

  autoTable(doc, {
    startY: yPosition,
    head: [['Standard', 'Requirement', 'Severity', 'Status', 'Date']],
    body: findingsTableData,
    styles: {
      fontSize: 8,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: 'bold',
    },
    bodyStyles: {
      textColor: 0,
    },
    columnStyles: {
      2: { cellWidth: 25 }, // Severity column
      3: { cellWidth: 25 }, // Status column
    },
    margin: { top: 10, right: 15, bottom: 10, left: 15 },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Detailed Findings Section
  findings.forEach((finding, index) => {
    if (yPosition > pageHeight - 40) {
      doc.addPage();
      yPosition = 15;
    }

    doc.setFont('Arial', 'bold');
    doc.setFontSize(11);
    doc.text(`Finding ${index + 1}: ${finding.standard || 'N/A'}`, 15, yPosition);

    yPosition += 7;
    doc.setFont('Arial', 'normal');
    doc.setFontSize(9);

    const detailText = [
      { label: 'Requirement:', value: finding.requirement || 'N/A', yOffset: 0 },
      { label: 'Severity:', value: getSeverityLabel(finding.severity), yOffset: 5 },
      { label: 'Status:', value: finding.status || 'N/A', yOffset: 5 },
      {
        label: 'Description:',
        value: finding.description || 'N/A',
        yOffset: 5,
        wrap: true,
      },
      {
        label: 'Evidence:',
        value: finding.evidence || 'N/A',
        yOffset: 5,
        wrap: true,
      },
      {
        label: 'Root Cause:',
        value: finding.rootCause || 'N/A',
        yOffset: 5,
        wrap: true,
      },
    ];

    detailText.forEach((item) => {
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 15;
      }

      doc.setFont('Arial', 'bold');
      doc.setFontSize(8);
      doc.text(item.label, 15, yPosition);

      doc.setFont('Arial', 'normal');
      const textLines = doc.splitTextToSize(item.value, pageWidth - 30);
      doc.text(textLines, 20, yPosition + 2);

      yPosition = yPosition + item.yOffset + (textLines.length > 1 ? textLines.length * 2 : 4);
    });

    yPosition += 5;

    // Corrective Actions
    if (finding.correctiveActions && finding.correctiveActions.length > 0) {
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 15;
      }

      doc.setFont('Arial', 'bold');
      doc.setFontSize(9);
      doc.text('Corrective Actions:', 15, yPosition);
      yPosition += 5;

      const caData = finding.correctiveActions.map((ca) => [
        truncateText(ca.description, 25),
        ca.ownerName || 'N/A',
        ca.targetDate ? new Date(ca.targetDate).toLocaleDateString() : 'N/A',
        ca.status || 'N/A',
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Description', 'Owner', 'Target Date', 'Status']],
        body: caData,
        styles: {
          fontSize: 7,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [52, 152, 219],
          textColor: 255,
          fontStyle: 'bold',
        },
        margin: { left: 15, right: 15 },
      });

      yPosition = doc.lastAutoTable.finalY + 5;
    }

    yPosition += 5;
  });

  // Footer
  const pageCount = doc.internal.pages.length;
  for (let i = 2; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('Arial', 'normal');
    doc.setFontSize(8);
    doc.text(
      `Page ${i - 1} of ${pageCount - 1}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  return doc;
};

/**
 * Generate Excel workbook with findings data
 * @param {array} findings - Array of finding objects
 * @param {object} project - Project information { id, name }
 * @returns {Workbook} - XLSX workbook
 */
export const generateExcel = (findings = [], project = {}) => {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Summary
  const summaryData = [
    ['Audit Findings Report'],
    [],
    ['Project:', project.name || 'N/A'],
    ['Date:', new Date().toLocaleDateString()],
    ['Report Generated:', new Date().toLocaleString()],
    [],
    ['Statistics'],
  ];

  const stats = calculateStatistics(findings);
  summaryData.push([
    'Total Findings',
    stats.total,
    'Critical',
    stats.critical,
    'Major',
    stats.major,
    'Minor',
    stats.minor,
  ]);
  summaryData.push([
    'Open',
    stats.open,
    'In Progress',
    stats.inProgress,
    'Resolved',
    stats.resolved,
    'Closed',
    stats.closed,
  ]);

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [
    { wch: 20 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
  ];

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Sheet 2: Findings
  const findingsData = findings.map((f) => [
    f.id || '',
    f.standard || '',
    f.requirement || '',
    getSeverityLabel(f.severity),
    f.status || '',
    f.description || '',
    f.evidence || '',
    f.rootCause || '',
    f.createdDate ? new Date(f.createdDate).toLocaleDateString() : '',
  ]);

  const findingsSheet = XLSX.utils.aoa_to_sheet([
    ['ID', 'Standard', 'Requirement', 'Severity', 'Status', 'Description', 'Evidence', 'Root Cause', 'Date'],
    ...findingsData,
  ]);

  // Format findings sheet
  findingsSheet['!cols'] = [
    { wch: 10 },
    { wch: 15 },
    { wch: 30 },
    { wch: 15 },
    { wch: 15 },
    { wch: 25 },
    { wch: 25 },
    { wch: 25 },
    { wch: 12 },
  ];

  // Apply header styling
  const headerRow = findingsSheet['!ref'].split(':')[0].match(/[A-Z]+/)[0];
  for (let col = 'A'; col <= 'I'; col++) {
    const cellRef = col + '1';
    if (findingsSheet[cellRef]) {
      findingsSheet[cellRef].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '2980B9' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      };
    }
  }

  XLSX.utils.book_append_sheet(workbook, findingsSheet, 'Findings');

  // Sheet 3: Corrective Actions
  const caData = [];
  findings.forEach((finding) => {
    if (finding.correctiveActions && finding.correctiveActions.length > 0) {
      finding.correctiveActions.forEach((ca) => {
        caData.push([
          finding.id || '',
          finding.standard || '',
          ca.id || '',
          ca.description || '',
          ca.ownerName || '',
          ca.targetDate ? new Date(ca.targetDate).toLocaleDateString() : '',
          ca.status || '',
          ca.completionDate ? new Date(ca.completionDate).toLocaleDateString() : '',
          ca.completionDate && ca.targetDate && new Date(ca.completionDate) > new Date(ca.targetDate) ? 'Overdue' : '',
        ]);
      });
    }
  });

  const caSheet = XLSX.utils.aoa_to_sheet([
    ['Finding ID', 'Standard', 'CA ID', 'Description', 'Owner', 'Target Date', 'Status', 'Completion Date', 'Overdue'],
    ...caData,
  ]);

  caSheet['!cols'] = [
    { wch: 12 },
    { wch: 15 },
    { wch: 12 },
    { wch: 30 },
    { wch: 20 },
    { wch: 12 },
    { wch: 15 },
    { wch: 15 },
    { wch: 12 },
  ];

  XLSX.utils.book_append_sheet(workbook, caSheet, 'Corrective Actions');

  return workbook;
};

/**
 * Calculate findings statistics
 */
const calculateStatistics = (findings) => {
  return {
    total: findings.length,
    critical: findings.filter((f) => f.severity === 'CRITICAL').length,
    major: findings.filter((f) => f.severity === 'MAJOR').length,
    minor: findings.filter((f) => f.severity === 'MINOR').length,
    open: findings.filter((f) => f.status === 'OPEN').length,
    inProgress: findings.filter((f) => f.status === 'IN_PROGRESS').length,
    resolved: findings.filter((f) => f.status === 'RESOLVED').length,
    closed: findings.filter((f) => f.status === 'CLOSED').length,
  };
};

/**
 * Truncate text to specified length
 */
const truncateText = (text, length = 30) => {
  if (!text) return 'N/A';
  return text.length > length ? text.substring(0, length) + '...' : text;
};
